import type {
  BillingInterval,
  BillingProduct,
} from '@/features/system-settings/types/system-settings'
import type { SubscriptionStatus } from '@/features/dashboard/types/dashboard'

/** Shape of `SubscriptionResource`: an organization's subscription to one product. */
export interface Subscription {
  id: number
  organization_id: number
  product: BillingProduct
  billing_interval: BillingInterval | null
  status: SubscriptionStatus
  has_access: boolean
  /** What the current period is billed for: active merchants (Moose CSS) or active websites (White Label). */
  quantity: number
  trial_ends_at: number | null
  trial_deferred: boolean
  cancel_at_period_end: boolean
}

/**
 * A row in these states has no running provider subscription, so the product can be chosen again:
 * `incomplete` only means a trial's checkout was opened but not completed, `canceled` that it ended.
 */
const CHOOSABLE_STATUSES: readonly SubscriptionStatus[] = ['incomplete', 'canceled']

export function isLiveSubscription(subscription: Subscription): boolean {
  return !CHOOSABLE_STATUSES.includes(subscription.status)
}

/**
 * Whether the provider's portal has anything to manage. An `incomplete` row only means that a
 * trial's checkout wasn't completed; a `canceled` one still has invoices and a saved card.
 */
export function hasBillingHistory(subscription: Subscription): boolean {
  return subscription.status !== 'incomplete'
}

/**
 * One step of a product's volume pricing (the backend's `billing_tiers_*` system settings). Amounts are
 * in cents per billing interval, and the tier covers quantities up to and including `up_to`.
 */
export interface BillingTier {
  up_to: number
  unit_amount: number
  flat_amount: number
}

/** Shape of `GET /organizations/{organization}/subscriptions/tiers`. */
export type BillingTiers = Record<BillingProduct, Record<BillingInterval, BillingTier[]>>

/** The answers to the optional question that suggests a plan on the billing tab. */
export const BILLING_ONBOARDING_ANSWERS = ['agency', 'ecommerce', 'affiliate', 'other'] as const
export type BillingOnboardingAnswer = (typeof BILLING_ONBOARDING_ANSWERS)[number]

/** The plan each answer suggests; `other` suggests none. */
export const RECOMMENDED_PRODUCTS: Record<BillingOnboardingAnswer, BillingProduct | null> = {
  agency: 'white_label_css',
  ecommerce: 'moose_css',
  affiliate: 'white_label_css',
  other: null,
}
